/**
 * Opening hours — live "open / closed" status.
 *
 * Liquid renders the schedule in the `slovan-opening-hours` snippet; this script only
 * works out the current status in the browser. That is deliberate: pages are cached,
 * so a server-side "now" can be hours stale. The time is always computed in the shop's
 * timezone (Europe/Bratislava), not the visitor's — otherwise a fan in London would see
 * "closed" an hour early.
 */
(function () {
  // The snippet renders in several places at once (top bar, footer, page), so the same
  // file can land in the page more than once. One run is enough.
  if (window.slovanOpeningHoursReady) return;
  window.slovanOpeningHoursReady = true;

  var WEEKDAY_TO_INDEX = { mon: 0, tue: 1, wed: 2, thu: 3, fri: 4, sat: 5, sun: 6 };
  // Fallback in case the settings are empty — the sentences must always read sensibly.
  var DAY_NAMES_IN = ['v pondelok', 'v utorok', 'v stredu', 'vo štvrtok', 'v piatok', 'v sobotu', 'v nedeľu'];
  var FALLBACK_TEXTS = {
    open: 'Práve otvorené · dnes do [čas]',
    openShort: 'Otvorené do [čas]',
    closed: 'Práve zatvorené · otvárame [deň] o [čas]',
    closedShort: 'Zatvorené · otvárame [deň] o [čas]',
    closedAll: 'Dnes [zatvorené]',
    today: 'dnes',
  };
  var CLOSED = '-';

  /** Fills placeholders like [čas] into a text template from the settings. */
  function fill(template, fallback, values) {
    var out = template || fallback;
    Object.keys(values).forEach(function (key) {
      out = out.split(key).join(values[key]);
    });
    return out;
  }

  /**
   * "11:00 - 18:00", "11.00-18.00", "11-18", "11:00 – 18:00 Uhr", "11:00 AM – 6:00 PM"
   * → { from: 660, to: 1080 }. Translated hour fields come in the translator's own format.
   */
  function parseRange(value) {
    if (!value || value === CLOSED) return null;
    var match = String(value).match(
      /(\d{1,2})(?:[:.](\d{2}))?\s*(a\.?m\.?|p\.?m\.?)?\s*[-–—]\s*(\d{1,2})(?:[:.](\d{2}))?\s*(a\.?m\.?|p\.?m\.?)?/i
    );
    if (!match) return null;

    var from = toMinutes(match[1], match[2], match[3]);
    var to = toMinutes(match[4], match[5], match[6]);
    if (isNaN(from) || isNaN(to) || to <= from) return null;

    return { from: from, to: to };
  }

  function toMinutes(hours, mins, meridiem) {
    var h = Number(hours);
    if (meridiem) {
      var pm = /^p/i.test(meridiem);
      h = (h % 12) + (pm ? 12 : 0);
    }
    return h * 60 + Number(mins || 0);
  }

  // English hour fields are written as "11:00 AM – 6:00 PM"; the live status then uses the
  // same 12-hour style instead of 24-hour times.
  var use12h = false;

  function formatMinutes(minutes) {
    var hours = Math.floor(minutes / 60);
    var mins = (minutes % 60 < 10 ? '0' : '') + (minutes % 60);
    if (!use12h) return hours + ':' + mins;
    return ((hours % 12) || 12) + ':' + mins + ' ' + (hours < 12 ? 'AM' : 'PM');
  }

  /** Current time in the shop's timezone. */
  function nowInZone(timeZone) {
    var parts;
    try {
      parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: timeZone,
        weekday: 'short',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).formatToParts(new Date());
    } catch (error) {
      // An unknown zone (e.g. a translated "Europa/Bratislava") must not switch the
      // status off — fall back to the shop's real zone once.
      if (timeZone !== 'Europe/Bratislava') return nowInZone('Europe/Bratislava');
      return null;
    }

    function get(type) {
      var part = parts.find(function (item) {
        return item.type === type;
      });
      return part ? part.value : '';
    }

    var dayIndex = WEEKDAY_TO_INDEX[get('weekday').toLowerCase().slice(0, 3)];
    if (dayIndex === undefined) return null;

    return {
      dayIndex: dayIndex,
      iso: get('year') + '-' + get('month') + '-' + get('day'),
      // some browsers return "24" at midnight
      minutes: (Number(get('hour')) % 24) * 60 + Number(get('minute')),
    };
  }

  /** Shifts an ISO date by N days. */
  function shiftIso(iso, days) {
    var date = new Date(iso + 'T12:00:00Z');
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
  }

  /** Hours for a given day — an exception overrides the regular schedule. */
  function hoursFor(schedule, iso, dayIndex) {
    if (schedule.exceptions && Object.prototype.hasOwnProperty.call(schedule.exceptions, iso)) {
      return schedule.exceptions[iso];
    }
    return schedule.week[dayIndex];
  }

  /** Next opening within 14 days: { dayIndex, from, isToday } or null. */
  function nextOpening(schedule, now) {
    for (var offset = 0; offset <= 14; offset++) {
      var iso = offset === 0 ? now.iso : shiftIso(now.iso, offset);
      var dayIndex = (now.dayIndex + offset) % 7;
      var range = parseRange(hoursFor(schedule, iso, dayIndex));
      if (!range) continue;
      if (offset === 0 && now.minutes >= range.from) continue;
      return { dayIndex: dayIndex, from: range.from, isToday: offset === 0 };
    }
    return null;
  }

  function describe(schedule, now, isCompact) {
    var todayRange = parseRange(hoursFor(schedule, now.iso, now.dayIndex));
    var closedLabel = schedule.closedLabel || 'zatvorené';
    var texts = schedule.texts || {};
    var daysIn = schedule.daysIn && schedule.daysIn.length === 7 ? schedule.daysIn : DAY_NAMES_IN;

    if (todayRange && now.minutes >= todayRange.from && now.minutes < todayRange.to) {
      return {
        isOpen: true,
        text: fill(
          isCompact ? texts.openShort : texts.open,
          isCompact ? FALLBACK_TEXTS.openShort : FALLBACK_TEXTS.open,
          { '[čas]': formatMinutes(todayRange.to), '[hodiny]': formatMinutes(todayRange.from) + ' - ' + formatMinutes(todayRange.to) }
        ),
      };
    }

    var next = nextOpening(schedule, now);
    if (!next) {
      return {
        isOpen: false,
        text: fill(texts.closedAll, FALLBACK_TEXTS.closedAll, { '[zatvorené]': closedLabel }),
      };
    }

    var when = next.isToday ? texts.today || FALLBACK_TEXTS.today : daysIn[next.dayIndex];
    return {
      isOpen: false,
      text: fill(
        isCompact ? texts.closedShort : texts.closed,
        isCompact ? FALLBACK_TEXTS.closedShort : FALLBACK_TEXTS.closed,
        { '[deň]': when, '[čas]': formatMinutes(next.from), '[zatvorené]': closedLabel }
      ),
    };
  }

  function update(root) {
    var dataNode = root.querySelector('[data-slovan-oh-data]');
    if (!dataNode) return;

    var schedule;
    try {
      schedule = JSON.parse(dataNode.textContent);
    } catch (error) {
      return;
    }
    if (!schedule || !schedule.week) return;

    use12h = schedule.week.some(function (value) {
      return /\d\s*[ap]\.?m\.?/i.test(String(value));
    });

    var now = nowInZone(schedule.tz || 'Europe/Bratislava');
    if (!now) return;

    var state = describe(schedule, now, root.classList.contains('slovan-oh--today'));

    var dot = root.querySelector('[data-oh-dot]');
    if (dot) dot.classList.toggle('is-open', state.isOpen);

    var status = root.querySelector('[data-oh-status]');
    if (status) {
      status.classList.toggle('is-open', state.isOpen);
      status.setAttribute('role', 'status');
      status.setAttribute('aria-label', state.text);
    }

    var text = root.querySelector('[data-oh-today-text]');
    if (text) text.textContent = state.text;

    // Re-apply today's row highlight here too — the cache may have stored the page yesterday.
    root.querySelectorAll('[data-oh-day]').forEach(function (row) {
      row.classList.toggle('is-today', Number(row.dataset.ohDay) === now.dayIndex);
    });
  }

  function updateAll(scope) {
    (scope || document).querySelectorAll('[data-slovan-oh]').forEach(update);
  }

  function init() {
    updateAll();
    window.setInterval(function () {
      updateAll();
    }, 60000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    updateAll(event.target);
  });
})();
